import { webappClient } from "../clients";
import { validatePayload, validateResponse } from "../validation";
import { z } from "zod";
import { sendErrorReport } from "v2/modules/reporting";

const tripRequestSchema = z.string();

const airportSchema = z.object({
  name: z.string(),
  code: z.object({
    iata: z.string(),
  }),
  city: z.object({
    display_name: z.string(),
  }),
  country: z.object({
    code: z.string(),
    display_name: z.string(),
  }),
  coordinates: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }),
});

const airlineSchema = z.object({
  identifier: z.string(),
  code: z.object({
    iata: z.string().nullable(),
    icao: z.string().nullable(),
  }),
  name: z.string(),
  display_name: z.string(),
});

export const flightSchema = z.object({
  identifier: z.string(),
  number: z.string(),
  airline: airlineSchema,
  departure_airport: airportSchema,
  arrival_airport: airportSchema,
  local_departure_date: z.string(),
  status: z.enum(["past", "active", "future"]),
  enriched_data: z
    .object({
      distance_km: z.number(),
      departure: z.object({
        terminal: z.object({
          scheduled: z.string().nullable(),
          actual: z.string().nullable(),
        }),
        gate: z.object({
          actual: z.string().nullable(),
        }),
        delay_mins: z.number().nullable(),
        actual_time: z.string().nullable(),
        scheduled_time: z.string().nullable(),
      }),
      arrival: z.object({
        terminal: z.object({
          scheduled: z.string().nullable(),
          actual: z.string().nullable(),
        }),
        gate: z.object({
          actual: z.string().nullable(),
        }),
        delay_mins: z.number().nullable(),
        actual_time: z.string().nullable(),
        estimated_time: z.string().nullable(),
        scheduled_time: z.string().nullable(),
        baggage_belt: z.string().nullable(),
      }),
      flight_status: z
        .object({
          status: z
            .enum([
              "on_time",
              "delayed",
              "cancelled",
              "landed",
              "landed_delayed",
              "landed_diverted",
              "unknown",
            ])
            .nullable(),
          delay_mins: z.number().nullable(),
        })
        .nullable(),
    })
    .nullable(),
});

export const tripResponseSchema = z.object({
  id: z.number(),
  flights: z.array(flightSchema).min(1),
  disruption_info: z
    .object({
      disrupted_flight_identifier: z.string(),
      regulation: z.enum([
        "ec261",
        "uk261",
        "brazil",
        "tr_shy",
        "ca_goodwill",
        "us_goodwill",
        "mcxx",
        "saudi_arabia",
      ]),
      disruption_reason: z.enum(["delayed", "cancelled", "missed_connection", "diverted"]),
      delay_mins: z.number().nullable(),
      potential_compensation: z.object({
        amount: z.number(),
        currency_code: z.string(),
      }),
    })
    .nullable(),
});

type TripResponse = z.infer<typeof tripResponseSchema>;

export async function fetchTrip(itineraryId: string): Promise<TripResponse | undefined> {
  validatePayload({
    data: itineraryId,
    schema: tripRequestSchema,
    tag: "Trip request payload",
  });

  try {
    const data = await webappClient
      .get(`api/mobile/v1/itineraries/${itineraryId}/disruption_details`)
      .json();

    const result = validateResponse({
      data: data,
      schema: tripResponseSchema,
      tag: "Trip API response",
    });

    return result;
  } catch (error) {
    sendErrorReport(error, "Failed to fetch trip data");
    return;
  }
}
